// src/pages/GoogleAds.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Badge, Alert, AlertDescription, LastUpdated,
} from '../components/ui';
import { formatCurrency, formatNumber } from '../lib/utils';
import {
  Plus, Trash2, RefreshCw, Settings, Save, X,
  ChevronLeft, ChevronRight, Check, Loader2, LogIn, Unplug,
  Database, BarChart3, EyeOff, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonAdPlatform } from '../components/ui/Skeleton';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

const getMonthLabel = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const getMonthRange = (year, month) => ({
  start: new Date(year, month, 1).toISOString().split('T')[0],
  end:   new Date(year, month + 1, 0).toISOString().split('T')[0],
});

export default function GoogleAds() {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts]           = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [dailyData, setDailyData]         = useState([]);
  const [monthOffset, setMonthOffset]     = useState(0);
  const [connecting, setConnecting]       = useState(false);
  const [fetching, setFetching]           = useState(false);
  const [refreshingCampaigns, setRefreshingCampaigns] = useState(false);
  const [showCampaignsModal, setShowCampaignsModal] = useState(false);
  const [accountCampaigns, setAccountCampaigns] = useState([]);
  const [campaignSelection, setCampaignSelection] = useState({}); // { campaignId: true/false }
  const [savingCampaigns, setSavingCampaigns] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [oauthTokens, setOauthTokens]     = useState(null);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  // "Add Account" asks how first, rather than two competing header buttons.
  const [showAddChooser, setShowAddChooser]   = useState(false);
  const [fetchingAll, setFetchingAll]         = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showDeleteEntryModal, setShowDeleteEntryModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [saving, setSaving]               = useState(false);
  const [fetchDebug, setFetchDebug]       = useState(null); // admin-only debug log

  const [accountForm, setAccountForm] = useState({ accountName: '', currency: 'USD', exchangeRate: '1.0', taxRate: '0', showOnDashboard: true });
  const [manualForm, setManualForm]   = useState({ date: new Date().toISOString().split('T')[0], spend: '', impressions: '', clicks: '', conversions: '' });

  // ── Load accounts ────────────────────────────────────────────────────────────
  const loadAccounts = useCallback(async () => {
    if (!user?.id) return;
    if (!user) return;
    try {
      const { data } = await supabase.from('google_ads_accounts').select('*').eq('user_id', user.id).order('created_at');
      setAccounts(data || []);
      // Auto-select: keep current selection if it still exists, otherwise pick first
      if (data?.length) {
        setSelectedAccount(prev => {
          if (!prev) return data[0];
          const stillExists = data.find(a => a.id === prev.id);
          return stillExists ? stillExists : data[0]; // refresh stale data
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.id]);

  // ── Load daily data ──────────────────────────────────────────────────────────
  const getViewMonth = useCallback(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - monthOffset);
    return d;
  }, [monthOffset]);

  const loadDailyData = useCallback(async () => {
    if (!selectedAccount) return;
    const vm = getViewMonth();
    const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
    const { data } = await supabase.from('google_ads_daily').select('*').eq('account_id', selectedAccount.id).gte('date', start).lte('date', end).order('date', { ascending: false });
    setDailyData(data || []);
  }, [selectedAccount, getViewMonth]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useVisibilityRefresh(loadAccounts);
  useEffect(() => { loadDailyData(); }, [loadDailyData]);

  // ── OAuth callback ───────────────────────────────────────────────────────────
  useEffect(() => {
    const code  = searchParams.get('code');
    const error = searchParams.get('error');
    if (error) { toast.error(`OAuth error: ${error}`); setSearchParams({}); return; }
    if (code && user) { handleOAuthCallback(code); setSearchParams({}); }
  }, [searchParams, user]);

  useEffect(() => {
    const was = sessionStorage.getItem('google_ads_connecting');
    if (was && !searchParams.get('code')) { sessionStorage.removeItem('google_ads_connecting'); setConnecting(false); }
  }, [searchParams]);

  const handleOAuthCallback = async (code) => {
    setConnecting(true);
    const tid = toast.loading('Fetching available accounts...');
    try {
      const res  = await fetch('/api/google-ads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'listAccounts', code, userId: user.id }) });
      const data = await res.json();
      if (data.success && data.accounts) {
        toast.dismiss(tid);
        setAvailableAccounts(data.accounts);
        setOauthTokens({ access_token: data.access_token, refresh_token: data.refresh_token, expires_at: data.expires_at });
        setShowAccountsModal(true);
      } else {
        toast.error(data.error || 'Connection failed', { id: tid });
      }
    } catch (e) { toast.error(e.message, { id: tid }); }
    setConnecting(false);
  };

  const selectApiAccount = async (account) => {
    const tid = toast.loading(`Connecting to ${account.name}...`);
    try {
      const res  = await fetch('/api/google-ads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'selectAccount', userId: user.id, account, tokens: oauthTokens }) });
      const data = await res.json();
      if (data.success) {
        toast.success(`Connected to ${account.name}!`, { id: tid });
        setShowAccountsModal(false); setAvailableAccounts([]); setOauthTokens(null);
        // Reload accounts — loadAccounts will auto-select the new one
        await loadAccounts();
        // Also refresh campaigns in background
        if (data.accountId) fetch('/api/google-ads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'refreshCampaigns', userId: user.id, accountId: data.accountId }) }).catch(() => {});
      } else { toast.error(data.error || 'Failed to connect', { id: tid }); }
    } catch (e) { toast.error(e.message, { id: tid }); }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res  = await fetch('/api/google-ads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getAuthUrl', userId: user.id }) });
      const data = await res.json();
      if (data.success && data.authUrl) { sessionStorage.setItem('google_ads_connecting', 'true'); window.location.href = data.authUrl; }
      else { toast.error(data.error || 'Failed to get auth URL'); setConnecting(false); }
    } catch (e) { toast.error(e.message); setConnecting(false); }
  };

  const handleDisconnect = async () => {
    if (!selectedAccount || !window.confirm('Disconnect API? Your data will be kept.')) return;
    try {
      await supabase.from('google_ads_accounts').update({ is_connected: false, access_token: null, refresh_token: null, token_expires_at: null }).eq('id', selectedAccount.id);
      toast.success('Disconnected'); loadAccounts();
    } catch (e) { toast.error(e.message); }
  };

  // ── Fetch spend data ─────────────────────────────────────────────────────────
  const handleFetch = async () => {
    if (!selectedAccount?.is_connected) return;
    setFetching(true);
    setFetchDebug(null);
    const tid = toast.loading('Fetching data from Google Ads...');
    try {
      const res  = await fetch('/api/google-ads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'fetch', userId: user.id, accountId: selectedAccount.id }) });
      const data = await res.json();
      if (data.logs) setFetchDebug({ log: data.logs, error: data.success ? null : data.error });
      if (data.success) {
        toast.success(`Fetched ${data.daysUpdated || 0} days of data`, { id: tid });
        loadDailyData();
      } else {
        toast.error(data.error || 'Fetch failed — see debug log', { id: tid, duration: 8000 });
      }
    } catch (e) {
      setFetchDebug({ log: [`Client error: ${e.message}`], error: e.message });
      toast.error(e.message, { id: tid });
    }
    setFetching(false);
  };

  // Fetches every connected account. handleFetch above only covers the one
  // currently selected, which isn't obvious from a header button.
  const handleFetchAll = async () => {
    const connected = accounts.filter(a => a.is_connected);
    if (!connected.length) { toast.error('No connected accounts'); return; }
    setFetchingAll(true);
    const tid = toast.loading(`Fetching ${connected.length} account(s)...`);
    let ok = 0; const errs = [];
    for (const a of connected) {
      try {
        const res = await fetch('/api/google-ads', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'fetch', userId: user.id, accountId: a.id }),
        });
        const data = await res.json();
        if (data.success) ok++; else errs.push(`${a.account_name || a.customer_id}: ${data.error}`);
      } catch (e) { errs.push(`${a.account_name || a.customer_id}: ${e.message}`); }
    }
    if (errs.length) toast.error(`${ok} ok, ${errs.length} failed — ${errs[0]}`, { id: tid, duration: 8000 });
    else toast.success(`Fetched ${ok} account(s)`, { id: tid });
    loadDailyData();
    setFetchingAll(false);
  };

  // ── Open campaigns modal ───────────────────────────────────────────────────
  const openCampaignsModal = async () => {
    if (!selectedAccount) return;
    setShowCampaignsModal(true);
    setRefreshingCampaigns(true);
    try {
      const { data: camps } = await supabase
        .from('google_ads_campaigns')
        .select('*')
        .eq('account_id', selectedAccount.id)
        .order('campaign_name', { ascending: true });
      setAccountCampaigns(camps || []);
      // Build selection: excluded = false means included
      const sel = {};
      (camps || []).forEach(c => { sel[c.campaign_id] = !c.excluded; });
      setCampaignSelection(sel);
    } catch (e) { toast.error('Failed to load campaigns'); }
    setRefreshingCampaigns(false);
  };

  const handleRefreshCampaigns = async () => {
    if (!selectedAccount?.is_connected) return;
    setRefreshingCampaigns(true);
    const tid = toast.loading('Refreshing campaigns from Google...');
    try {
      const res  = await fetch('/api/google-ads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'refreshCampaigns', userId: user.id, accountId: selectedAccount.id }) });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.count || 0} campaigns loaded`, { id: tid });
        // Reload from DB
        const { data: camps } = await supabase.from('google_ads_campaigns').select('*').eq('account_id', selectedAccount.id).order('campaign_name', { ascending: true });
        setAccountCampaigns(camps || []);
        const sel = {};
        (camps || []).forEach(c => { sel[c.campaign_id] = !c.excluded; });
        setCampaignSelection(sel);
      } else toast.error(data.error || 'Failed', { id: tid });
    } catch (e) { toast.error(e.message, { id: tid }); }
    setRefreshingCampaigns(false);
  };

  const toggleCampaign = (campaignId) => {
    setCampaignSelection(prev => ({ ...prev, [campaignId]: !prev[campaignId] }));
  };

  const selectAllCampaigns  = () => { const s = {}; accountCampaigns.forEach(c => { s[c.campaign_id] = true;  }); setCampaignSelection(s); };
  const deselectAllCampaigns = () => { const s = {}; accountCampaigns.forEach(c => { s[c.campaign_id] = false; }); setCampaignSelection(s); };

  const saveCampaignSelection = async () => {
    setSavingCampaigns(true);
    const tid = toast.loading('Saving...');
    try {
      // Update each campaign's excluded flag
      for (const c of accountCampaigns) {
        await supabase.from('google_ads_campaigns')
          .update({ excluded: !campaignSelection[c.campaign_id] })
          .eq('id', c.id);
      }
      toast.success('Campaign selection saved', { id: tid });
      setShowCampaignsModal(false);
    } catch (e) { toast.error(e.message, { id: tid }); }
    setSavingCampaigns(false);
  };

  // ── Create manual account ───────────────────────────────────────────────────
  const handleCreateManualAccount = async () => {
    if (!accountForm.accountName.trim()) { toast.error('Account name required'); return; }
    setSaving(true);
    try {
      const { data: newAcc, error } = await supabase.from('google_ads_accounts').insert({ user_id: user.id, account_name: accountForm.accountName.trim(), currency: accountForm.currency, exchange_rate: parseFloat(accountForm.exchangeRate) || 1, tax_rate: parseFloat(accountForm.taxRate) || 0, show_on_dashboard: accountForm.showOnDashboard, is_connected: false }).select().single();
      if (error) throw error;
      toast.success('Account created!');
      setShowCreateModal(false);
      setAccountForm({ accountName: '', currency: 'USD', exchangeRate: '1.0', taxRate: '0', showOnDashboard: true });
      await loadAccounts();
      if (newAcc) setSelectedAccount(newAcc);
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Save settings ────────────────────────────────────────────────────────────
  const handleSaveSettings = async () => {
    if (!selectedAccount) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('google_ads_accounts').update({ account_name: accountForm.accountName.trim() || selectedAccount.account_name, currency: accountForm.currency, exchange_rate: parseFloat(accountForm.exchangeRate) || 1, tax_rate: parseFloat(accountForm.taxRate) || 0, show_on_dashboard: accountForm.showOnDashboard, updated_at: new Date().toISOString() }).eq('id', selectedAccount.id);
      if (error) throw error;
      toast.success('Settings saved'); setShowSettingsModal(false); loadAccounts();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const openSettings = () => {
    if (!selectedAccount) return;
    setAccountForm({ accountName: selectedAccount.account_name || '', currency: selectedAccount.currency || 'USD', exchangeRate: selectedAccount.exchange_rate?.toString() || '1.0', taxRate: selectedAccount.tax_rate?.toString() || '0', showOnDashboard: selectedAccount.show_on_dashboard !== false });
    setShowSettingsModal(true);
  };

  // ── Manual entry ─────────────────────────────────────────────────────────────
  const handleManualEntry = async () => {
    if (!selectedAccount || !manualForm.date || !manualForm.spend) { toast.error('Date and spend are required'); return; }
    setSaving(true);
    try {
      const er  = parseFloat(selectedAccount.exchange_rate) || 1;
      const tr  = parseFloat(selectedAccount.tax_rate) || 0;
      const raw = parseFloat(manualForm.spend) || 0;
      const spend = raw * er * (1 + tr / 100);
      const { error } = await supabase.from('google_ads_daily').upsert({ user_id: user.id, account_id: selectedAccount.id, date: manualForm.date, spend, spend_original: raw, impressions: parseInt(manualForm.impressions) || 0, clicks: parseInt(manualForm.clicks) || 0, conversions: parseFloat(manualForm.conversions) || 0, source: 'manual' }, { onConflict: 'account_id,date', ignoreDuplicates: false });
      if (error) throw error;
      toast.success('Entry saved'); setShowManualModal(false); setManualForm({ date: new Date().toISOString().split('T')[0], spend: '', impressions: '', clicks: '', conversions: '' }); loadDailyData();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDeleteEntry = async () => {
    if (!selectedEntry) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('google_ads_daily').delete().eq('id', selectedEntry.id);
      if (error) throw error;
      toast.success('Entry deleted'); setShowDeleteEntryModal(false); setSelectedEntry(null); loadDailyData();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('google_ads_accounts').delete().eq('id', selectedAccount.id);
      if (error) throw error;
      toast.success('Account deleted'); setShowDeleteAccountModal(false); setSelectedAccount(null); loadAccounts();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  const totalSpend       = dailyData.reduce((s, d) => s + (d.spend || 0), 0);
  const totalImpressions = dailyData.reduce((s, d) => s + (d.impressions || 0), 0);
  const totalClicks      = dailyData.reduce((s, d) => s + (d.clicks || 0), 0);
  const totalConversions = dailyData.reduce((s, d) => s + (d.conversions || 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const vm = getViewMonth();
  const monthLabel = getMonthLabel(vm);

  if (loading) return <SkeletonAdPlatform />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Google Ads</h1>
          <p className="text-muted-foreground">{accounts.length} account{accounts.length !== 1 ? 's' : ''} configured</p>
          <LastUpdated
            value={accounts.reduce((latest, a) => {
              const t = a?.last_fetch || a?.updated_at;
              if (!t) return latest;
              return !latest || new Date(t) > new Date(latest) ? t : latest;
            }, null)}
            className="mt-2"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleFetchAll} disabled={fetchingAll}>
            {fetchingAll ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {fetchingAll ? 'Fetching...' : 'Fetch All'}
          </Button>
          <Button variant="outline" onClick={() => setShowManualModal(true)}>Manual Entry</Button>
          <Button onClick={() => setShowAddChooser(true)} disabled={connecting}>
            {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {connecting ? 'Connecting...' : 'Add Account'}
          </Button>
        </div>
      </div>

      {/* Account cards */}
      {accounts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Google Ads Accounts</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">Connect via API for automatic syncing, or add a manual account to track spend.</p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => setShowAddChooser(true)}><Plus className="h-4 w-4 mr-2" />Add Account</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {accounts.map(account => (
            <Card key={account.id} className={`cursor-pointer transition-all ${selectedAccount?.id === account.id ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`} onClick={() => setSelectedAccount(account)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium truncate">{account.account_name}</h3>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium">
                      <AlertTriangle className="h-3 w-3" /> Error
                    </span>
                    <Badge variant={account.is_connected ? 'default' : 'outline'} className="text-xs">{account.is_connected ? 'API' : 'Manual'}</Badge>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">{account.customer_id ? `ID: ${account.customer_id}` : account.currency}</div>
                {!account.show_on_dashboard && <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2"><EyeOff className="h-3 w-3" />Hidden from overview</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Selected account detail */}
      {selectedAccount && (
        <>
          {/* Account header */}
          <div className="flex items-center justify-between border-t pt-6 flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold">{selectedAccount.account_name}</h2>
              <p className="text-sm text-muted-foreground">
                {selectedAccount.customer_id && `ID: ${selectedAccount.customer_id} · `}
                {selectedAccount.currency} · {selectedAccount.tax_rate || 0}% tax · rate {selectedAccount.exchange_rate || 1}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={openSettings}><Settings className="h-4 w-4 mr-1" />Settings</Button>
              {selectedAccount.is_connected && (
                <>
                  <Button variant="outline" size="sm" onClick={openCampaignsModal}>
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Campaigns
                    {accountCampaigns.length > 0 && (
                      <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                        {Object.values(campaignSelection).filter(Boolean).length}/{accountCampaigns.length}
                      </span>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleFetch} disabled={fetching}>
                    {fetching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                    Fetch
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleDisconnect} title="Disconnect API"><Unplug className="h-4 w-4" /></Button>
                </>
              )}
              {!selectedAccount.is_connected && (
                <Button variant="outline" size="sm" onClick={handleConnect}><LogIn className="h-4 w-4 mr-1" />Connect API</Button>
              )}
              <Button size="sm" onClick={() => setShowManualModal(true)}><Plus className="h-4 w-4 mr-1" />Add Entry</Button>
            </div>
          </div>

          {/* Admin-only fetch debug log */}
          {fetchDebug && (
            <div className={`rounded-xl border text-xs font-mono ${fetchDebug.error ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-inherit">
                <span className={`font-semibold text-sm ${fetchDebug.error ? 'text-red-700' : 'text-gray-700'}`}>
                  {fetchDebug.error ? `⚠ Fetch Error: ${fetchDebug.error}` : '✓ Fetch Debug Log'}

                </span>
                <button onClick={() => setFetchDebug(null)} className="text-gray-400 hover:text-gray-600 px-1 text-base leading-none">✕</button>
              </div>
              <div className="p-3 max-h-56 overflow-y-auto space-y-0.5">
                {(fetchDebug.log || []).map((line, i) => (
                  <div key={i} className={`leading-relaxed ${line.toLowerCase().includes('error') || line.toLowerCase().includes('fail') ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{line}</div>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid gap-4 md:grid-cols-5">
            {[
              { label: 'Total Spend', value: formatCurrency(totalSpend), cls: 'text-red-600' },
              { label: 'Impressions', value: formatNumber(totalImpressions) },
              { label: 'Clicks',      value: formatNumber(totalClicks) },
              { label: 'CTR',         value: `${avgCtr.toFixed(2)}%` },
              { label: 'Conversions', value: totalConversions.toFixed(0) },
            ].map(s => (
              <Card key={s.label}><CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{s.label}</p>
                <p className={`text-2xl font-bold ${s.cls || ''}`}>{s.value}</p>
                {s.label === 'Total Spend' && <p className="text-xs text-muted-foreground">{monthLabel}</p>}
              </CardContent></Card>
            ))}
          </div>

          {/* Month nav */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => setMonthOffset(monthOffset + 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="font-medium">{monthLabel}</span>
            <Button variant="outline" size="sm" onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} disabled={monthOffset === 0}><ChevronRight className="h-4 w-4" /></Button>
          </div>

          {/* Daily table */}
          <Card>
            <CardHeader><CardTitle>Daily Spend</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>{['Date','Spend','Impressions','Clicks','CTR','Conv.','Source',''].map(h => <th key={h} className="p-3 text-left text-xs font-medium text-muted-foreground">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {dailyData.length === 0 ? (
                      <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No data for {monthLabel}. {selectedAccount.is_connected ? 'Click Fetch to pull data.' : 'Add manual entries.'}</td></tr>
                    ) : dailyData.map(entry => (
                      <tr key={entry.id} className="border-t hover:bg-muted/20">
                        <td className="p-3 font-medium">{entry.date}</td>
                        <td className="p-3 text-red-600">{formatCurrency(entry.spend)}</td>
                        <td className="p-3">{formatNumber(entry.impressions)}</td>
                        <td className="p-3">{formatNumber(entry.clicks)}</td>
                        <td className="p-3">{entry.impressions > 0 ? `${((entry.clicks / entry.impressions) * 100).toFixed(2)}%` : '—'}</td>
                        <td className="p-3">{entry.conversions?.toFixed(1) ?? '—'}</td>
                        <td className="p-3"><Badge variant={entry.source === 'api' ? 'default' : 'outline'}>{entry.source === 'api' ? 'API' : 'Manual'}</Badge></td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedEntry(entry); setShowDeleteEntryModal(true); }}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setShowDeleteAccountModal(true)}>
              <Trash2 className="h-4 w-4 mr-2" />Delete Account
            </Button>
          </div>
        </>
      )}

      {/* ── OAuth account selection modal ── */}
      {showAccountsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader><CardTitle>Select Google Ads Account</CardTitle><CardDescription>Choose which account to connect ({availableAccounts.length} available)</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              {availableAccounts.map(account => (
                <button key={account.id} onClick={() => selectApiAccount(account)} className="w-full p-4 rounded-lg border hover:bg-accent text-left transition-colors">
                  <div className="font-medium">{account.name}</div>
                  <div className="text-sm text-muted-foreground">ID: {account.id} · {account.currency}</div>
                </button>
              ))}
              <Button variant="ghost" className="w-full mt-4" onClick={() => { setShowAccountsModal(false); setAvailableAccounts([]); setOauthTokens(null); }}>Cancel</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Create manual modal ── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader><CardTitle>Add Manual Account</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Account Name *</label><Input value={accountForm.accountName} onChange={e => setAccountForm(f => ({ ...f, accountName: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-sm font-medium">Currency</label>
                  <select value={accountForm.currency} onChange={e => setAccountForm(f => ({ ...f, currency: e.target.value }))} className="mt-1 w-full border rounded px-2 py-2 text-sm">
                    {['USD','GBP','EUR','CAD','AUD','NGN','ZAR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="text-sm font-medium">Rate</label><Input type="number" step="0.0001" value={accountForm.exchangeRate} onChange={e => setAccountForm(f => ({ ...f, exchangeRate: e.target.value }))} className="mt-1" /></div>
                <div><label className="text-sm font-medium">Tax %</label><Input type="number" step="0.01" value={accountForm.taxRate} onChange={e => setAccountForm(f => ({ ...f, taxRate: e.target.value }))} className="mt-1" /></div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={accountForm.showOnDashboard} onChange={e => setAccountForm(f => ({ ...f, showOnDashboard: e.target.checked }))} className="rounded" /><span className="text-sm">Show on overview</span></label>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleCreateManualAccount} disabled={saving}>{saving ? 'Creating...' : 'Create Account'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Settings modal ── */}
      {showSettingsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader><CardTitle>Account Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Account Name</label><Input value={accountForm.accountName} onChange={e => setAccountForm(f => ({ ...f, accountName: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-sm font-medium">Currency</label>
                  <select value={accountForm.currency} onChange={e => setAccountForm(f => ({ ...f, currency: e.target.value }))} className="mt-1 w-full border rounded px-2 py-2 text-sm">
                    {['USD','GBP','EUR','CAD','AUD','NGN','ZAR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="text-sm font-medium">Rate</label><Input type="number" step="0.0001" value={accountForm.exchangeRate} onChange={e => setAccountForm(f => ({ ...f, exchangeRate: e.target.value }))} className="mt-1" /></div>
                <div><label className="text-sm font-medium">Tax %</label><Input type="number" step="0.01" value={accountForm.taxRate} onChange={e => setAccountForm(f => ({ ...f, taxRate: e.target.value }))} className="mt-1" /></div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={accountForm.showOnDashboard} onChange={e => setAccountForm(f => ({ ...f, showOnDashboard: e.target.checked }))} className="rounded" /><span className="text-sm">Show on overview</span></label>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSaveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Manual entry modal ── */}
      {/* How do you want to add this account? One entry point instead of two
          competing header buttons — mirrors Meta Ads and TikTok Ads. */}
      {showAddChooser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAddChooser(false)}>
          <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Add Google Ads Account</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect via the Google Ads API for automatic syncing, or add a manual account to enter spend yourself.
                </p>
              </div>

              <button
                type="button"
                onClick={() => { setShowAddChooser(false); handleConnect(); }}
                disabled={connecting}
                className="w-full text-left rounded-xl border border-border hover:border-primary hover:bg-muted/40 transition-colors p-4 disabled:opacity-60"
              >
                <div className="flex items-start gap-3">
                  <LogIn className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Connect via Google Ads API</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Sign in with Google and pick an account. Spend and campaigns sync automatically.
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => { setShowAddChooser(false); setShowCreateModal(true); }}
                className="w-full text-left rounded-xl border border-border hover:border-primary hover:bg-muted/40 transition-colors p-4"
              >
                <div className="flex items-start gap-3">
                  <Database className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Add a manual account</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Track spend you enter yourself. You can connect the API to it later.
                    </p>
                  </div>
                </div>
              </button>

              <Button variant="outline" className="w-full" onClick={() => setShowAddChooser(false)}>Cancel</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {showManualModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader><CardTitle>Add Spend Entry</CardTitle><CardDescription>{selectedAccount?.account_name}</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Date *</label><Input type="date" value={manualForm.date} onChange={e => setManualForm(f => ({ ...f, date: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium">Spend *</label><Input type="number" step="0.01" value={manualForm.spend} onChange={e => setManualForm(f => ({ ...f, spend: e.target.value }))} placeholder="0.00" className="mt-1" /></div>
                <div><label className="text-sm font-medium">Impressions</label><Input type="number" value={manualForm.impressions} onChange={e => setManualForm(f => ({ ...f, impressions: e.target.value }))} placeholder="0" className="mt-1" /></div>
                <div><label className="text-sm font-medium">Clicks</label><Input type="number" value={manualForm.clicks} onChange={e => setManualForm(f => ({ ...f, clicks: e.target.value }))} placeholder="0" className="mt-1" /></div>
                <div><label className="text-sm font-medium">Conversions</label><Input type="number" step="0.1" value={manualForm.conversions} onChange={e => setManualForm(f => ({ ...f, conversions: e.target.value }))} placeholder="0" className="mt-1" /></div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowManualModal(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleManualEntry} disabled={saving}>{saving ? 'Saving...' : 'Save Entry'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Delete entry modal ── */}
      {showDeleteEntryModal && selectedEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader><CardTitle>Delete Entry</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">Delete entry for {new Date(selectedEntry.date).toLocaleDateString()}?</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowDeleteEntryModal(false)}>Cancel</Button>
                <Button variant="destructive" className="flex-1" onClick={handleDeleteEntry} disabled={saving}>{saving ? 'Deleting...' : 'Delete'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Delete account modal ── */}
      {showDeleteAccountModal && selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader><CardTitle>Delete Account</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">Delete "{selectedAccount.account_name}" and all its data? This cannot be undone.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowDeleteAccountModal(false)}>Cancel</Button>
                <Button variant="destructive" className="flex-1" onClick={handleDeleteAccount} disabled={saving}>{saving ? 'Deleting...' : 'Delete Account'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Campaigns modal ── */}
      {showCampaignsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[85vh] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Campaigns</CardTitle>
                  <p className="text-sm text-muted-foreground mt-0.5">{selectedAccount?.account_name}</p>
                </div>
                <button onClick={() => setShowCampaignsModal(false)} className="text-muted-foreground hover:text-foreground p-1 rounded"><X className="h-5 w-5" /></button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden flex flex-col space-y-3">
              {/* Controls */}
              <div className="flex items-center justify-between flex-shrink-0">
                <p className="text-sm text-muted-foreground">
                  {Object.values(campaignSelection).filter(Boolean).length} of {accountCampaigns.length} selected for reporting
                </p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllCampaigns}>All</Button>
                  <Button size="sm" variant="outline" onClick={deselectAllCampaigns}>None</Button>
                  <Button size="sm" variant="outline" onClick={handleRefreshCampaigns} disabled={refreshingCampaigns}>
                    {refreshingCampaigns ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Campaign list */}
              <div className="flex-1 overflow-y-auto border rounded-lg divide-y">
                {refreshingCampaigns && accountCampaigns.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading campaigns...
                  </div>
                ) : accountCampaigns.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No campaigns found. Click refresh to load from Google Ads.
                  </div>
                ) : (
                  accountCampaigns.map(c => (
                    <div
                      key={c.id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => toggleCampaign(c.campaign_id)}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        campaignSelection[c.campaign_id]
                          ? 'bg-primary border-primary'
                          : 'border-border bg-background'
                      }`}>
                        {campaignSelection[c.campaign_id] && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${!campaignSelection[c.campaign_id] ? 'text-muted-foreground line-through' : ''}`}>
                          {c.campaign_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{c.status}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-3 flex-shrink-0 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setShowCampaignsModal(false)}>Cancel</Button>
                <Button className="flex-1" onClick={saveCampaignSelection} disabled={savingCampaigns}>
                  {savingCampaigns ? 'Saving...' : 'Save Selection'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
